import React, { useEffect, useRef, useState } from 'react'
import filesize from 'filesize'
import moment from 'moment'
import { api } from 'core/api/ConnectionManager'
import helpers from 'core/helpers'
import Toast from 'blocks.simple/toast/toast'
import translate from 'core/translate'
import helpersDigitalSignages from 'core/helpers/_digitalSignages'
import { licenseType, rateTypeRename } from 'core/utils/displayUtil'
import displaysHelpers from '../../../core/helpers/_digitalSignages'
import { resolveLegacyTimezone } from 'core/helpers/timezone'
import type { MonitorWindowData } from 'blocks.simple/monitorWindow/MonitorWindow.types'
import type { ToastOptions } from 'blocks.simple/toast/toast.types'
import type { RouteComponentProps } from 'react-router-dom'

export type FormattedParameters = Record<string, string>

export type RefContent = {
    contentOpen: () => void
    contentClose: () => void
}

export interface BuildInfo extends Record<string, string | number | boolean | null> {
    buildType: string
    buildDatetime: string
    buildHash: string
}

export interface ExternalStorageInfo {
    externalStorages: Array<{
        size: {
            totalBytes: number
            usedBytes: number
            availableBytes: number
        }
        label: string
    }>
    externalStoragesUpdatedAt: string
}

interface LicenseResponseItem {
    activeQ: boolean
    licenseId: number
    vacantCount: number
    expiredAt: string | null
    paidQ: boolean
    rateType: string | null
}

interface LicenseOption {
    id: number
    name: string
    vacantCount: number
    expiredAt: string | null
    paidQ: boolean
}

export interface ISelectedInfo {
    currentlyPlaying: {
        entityObject: {
            id: number
            title: string
        }
    }[]
    groupId: number
    parentId: number
    id: number
    online: boolean
    platform: string
    osFamily?: string | null
    osFamilyVersion?: string | null
    osVersion?: string | null
    prettySysName?: string | null
    orientation: number
    errorCode: number
    description: string
    volume: number
    diagonal: string
    resolutionWidth: number
    resolutionHeight: number
    status: string
    type: string
    lastScreenshotDate: string
    licenseId: number | null
    tags: { name: string; id: string; value: string; label: string }[]
    cover: string[]
    newerClientVersion: string
    name: string
    identificationImplementedQ: boolean
    menu: { name: string }[]
    manufacturer: string
    settings: {
        enableIPTV?: boolean
        logLevel: string
        autoScreenshot: {
            width: number
            height: number
            enabled: boolean
            interval: number
        }
        comPorts: Array<{ Action: string; [key: string]: unknown }>
    }
    activeQ: boolean
    rootEnabled: boolean | undefined
    storageSize: number
    freeStorageSize: number
    usedStorageSize: number
    usedBytes: number
    reservedBytes: number
    connectionType: string
    firstDns: string
    ipAddress: string
    macAddress: string
    secondDns: string
    serverToDsRequestTimeMS: number
    clientToServerResponseTimeMS: number
    clientToServerRequestTimeMS: number
    downloadFromStorageSpeed: number
    timezone: string
    timezoneOffset: number
}

export type ISetMonitorWindow = MonitorWindowData

export interface IDisplayInfoProps extends RouteComponentProps {
    user: number
    selectedInfo: ISelectedInfo
    closeModal: () => void
    query: {
        mapquery: string
    }
    getClientLogs: (data: { id: number; actual: boolean }) => void
    isConnected: boolean
    setMonitorWindow: (data: ISetMonitorWindow) => void
    groups?: number[]
}

export type DSStateValue = string | number | string[] | null | undefined

export type DSState = {
    [key: string]: DSStateValue
    id: number
    startTime: string | null
    deviceStartTime: string | null
    currentDate: string | null
    timezone: string | null
    batteryLevel?: number
    powerSource?: 'Ac' | 'None' | 'Usb' | 'Wireless'
}

type DSStorageInfo = {
    digitalSignageId: number
    storageInfo: {
        storageSize: number
        freeStorageSize: number
        usedStorageSize: number
        appUsedStorageInfo: {
            usedBytes: number
            reservedBytes: number
        }
    }
}

export type GroupTreeItem = {
    id: number
    name: string
    digitalSignageCount: number
    nestedLevel: number
    parentId: number
    children: GroupTreeItem[]
}

export interface IState {
    showClearLogsWarning: boolean
    wakeOnLanModal: boolean
    active: string[]
    buildInfo: BuildInfo | null
    formattedParameters: FormattedParameters
    showCleanMenu: boolean
    removeAll: boolean
    times: {
        diffTimes: string
        diffDays: string
    } | null
    deviceTimes: {
        diffTimes: string
        diffDays: string
    } | null
    showAll: boolean
    deviceStartTime: string | null
    dsState: DSState | null
    isUpdatingScreenShot: boolean
    startTime: string | null
    getClientLogsMenu: boolean
    clean: { files?: boolean; database?: boolean; statistic?: boolean; logs?: boolean }
    externalStorages: ExternalStorageInfo | null
    showShutdownMenu?: boolean
    showDeleteMenu?: boolean
    licensesData?: LicenseOption[]
    currentLicense?: {
        activeQ: boolean
        expiredAt: null | string
        id: number
        name: string
        paidQ: boolean
        vacantCount: number
    } | null
    webEngineInfo: string
    showMore: boolean
    openPowerModal: string
    sleepModeModal: string
    storageInfoLoading: boolean
    networkInfoLoading: boolean
    storageInfo: DSStorageInfo | null
    networkInfo: {
        connectionType: string
        firstDns: string
        secondDns: string
        ipAddress: string
        macAddress: string
    } | null
    isConnectionSpeedLoading: boolean
    connectionSpeed: Partial<{
        serverToDsRequestTimeMS: number
        clientToServerResponseTimeMS: number
        clientToServerRequestTimeMS: number
        downloadFromStorageSpeed: number
    }> | null
    connectionSpeedStartTimestamp: number
    isStorageConnectionSpeedLoading: boolean
    hasStorageConnectionSpeedError: boolean
    toggleStatusList: {
        mainRef: boolean
        deviceRef: boolean
        licensesRef: boolean
        networkRef: boolean
        storageRef: boolean
        externalStorageRef: boolean
        buildRef: boolean
    }
    softResetModal: boolean
    groupTreeInfo: {
        total: {
            digitalSignagesCount: number
            groupsCount: number
        }
        list: GroupTreeItem[]
    } | null
    groupsOpened: { id: number; parentId: number }[]
    connectionError: boolean
}

function _formattingParameters(p_: ISelectedInfo, s_: IState) {
    const selectedInfo = { ...p_ }
    const { storageInfo } = { ...s_ }
    const { networkInfo } = { ...s_ }
    const { connectionSpeed } = { ...s_ }
    const parameters: Record<string, string | number> = {}
    const sizes = ['storageSize', 'usedStorageSize', 'freeStorageSize', 'usedBytes', 'reservedBytes']
    const dates = ['lastScreenshotDate', 'createdAt', 'lastConnection', 'updatedAt']

    if (selectedInfo.hasOwnProperty('platform') && selectedInfo.hasOwnProperty('diagonal')) {
        if (selectedInfo.platform === 'SSSP 2' || selectedInfo.platform === 'SSSP 3') {
            delete (selectedInfo as { diagonal?: string }).diagonal
        }
    }

    if (helpersDigitalSignages.isFolder(selectedInfo)) {
        delete (selectedInfo as { orientation?: number }).orientation
    }

    if (storageInfo) {
        selectedInfo.storageSize = storageInfo.storageInfo.storageSize
        selectedInfo.usedStorageSize = storageInfo.storageInfo.usedStorageSize
        selectedInfo.freeStorageSize = storageInfo.storageInfo.freeStorageSize
    }

    if (
        storageInfo &&
        storageInfo?.storageInfo?.appUsedStorageInfo &&
        selectedInfo.usedBytes &&
        selectedInfo.reservedBytes
    ) {
        selectedInfo.reservedBytes = storageInfo.storageInfo.appUsedStorageInfo.reservedBytes
        selectedInfo.usedBytes = storageInfo.storageInfo.appUsedStorageInfo.usedBytes
    }

    if (networkInfo) {
        selectedInfo.connectionType = networkInfo.connectionType
        selectedInfo.firstDns = networkInfo.firstDns
        selectedInfo.secondDns = networkInfo.secondDns
        selectedInfo.ipAddress = networkInfo.ipAddress
        selectedInfo.macAddress = networkInfo.macAddress
    }

    if (connectionSpeed) {
        if (connectionSpeed.serverToDsRequestTimeMS !== undefined) {
            selectedInfo.serverToDsRequestTimeMS = connectionSpeed.serverToDsRequestTimeMS
        }
        if (connectionSpeed.clientToServerResponseTimeMS !== undefined) {
            selectedInfo.clientToServerResponseTimeMS = connectionSpeed.clientToServerResponseTimeMS
        }
        if (connectionSpeed.clientToServerRequestTimeMS !== undefined) {
            selectedInfo.clientToServerRequestTimeMS = connectionSpeed.clientToServerRequestTimeMS
        }
        if (connectionSpeed.downloadFromStorageSpeed !== undefined) {
            selectedInfo.downloadFromStorageSpeed = connectionSpeed.downloadFromStorageSpeed
        }
    }

    const selectedInfoRecord = (selectedInfo as unknown) as Record<string, unknown>

    for (let key in selectedInfoRecord) {
        if (selectedInfo.hasOwnProperty(key)) {
            let selectedInfoKey = selectedInfoRecord[key]
            const parameter = selectedInfoKey

            if (key === 'rootEnabled') {
                if (selectedInfoKey !== null) {
                    parameters[key] = `<div>${
                        selectedInfoKey
                            ? translate('yes')
                            : `<div>${translate('no')}</div><span>${translate(
                                  'noRootWarning'
                              )} <a target="_blank" href="https://wiki.smartplayer.org/index.php/%D0%9E%D1%82%D1%81%D1%83%D1%82%D1%81%D1%82%D0%B2%D0%B8%D0%B5_root_%D0%B4%D0%BE%D1%81%D1%82%D1%83%D0%BF%D0%B0_%D1%83_%D0%BF%D1%80%D0%B8%D0%BB%D0%BE%D0%B6%D0%B5%D0%BD%D0%B8%D1%8F_SmartPlayer_%D0%BD%D0%B0_AndroidOS">${translate(
                                  'here'
                              )}</a></span>`
                    }</div>`
                }
            }

            if (Array.isArray(parameter)) {
                parameters[key] = parameter.join(', ')
            }

            if (typeof parameter === 'string' || typeof parameter === 'number') {
                let formattedValue: string | number = parameter

                if (sizes.includes(key)) {
                    if (Number(parameter) > 0) {
                        formattedValue = filesize(Number(parameter))
                    }
                }
                if (dates.includes(key)) {
                    formattedValue = helpers.getFormattedLocalDate(String(parameter), 'DD/MM/YYYY HH:mm') || ''
                }
                parameters[key] = formattedValue

                if (key === 'orientation') {
                    const orientation = helpersDigitalSignages.getOrientation(Number(parameters[key]))
                    if (orientation) {
                        parameters[key] = orientation.name || ''
                    }
                }
                if (key === 'rateType' || key === 'licenseType') {
                    parameters[key] = translate(String(parameters[key]))
                }
            }
        }
    }

    return sortedParams(parameters)
}

function sortedParams(parameters: Record<string, string | number>): FormattedParameters {
    const keys: string[] = [
        'licenseId',
        'licenseType',
        'rateType',
        'licensePurchaseDate',
        'licenseExpireDate',
        'id',
        'deviceId',
        'idCashRegisterGlobal',
        'dsVersion',
        'name',
        'description',
        'platform',
        'prettySysName',
        'applicationURL',
        'lastScreenshotDate',
        'lastScreenshot',
        'groupId',
        'companyId',
        'updatedAt',
        'createdAt',
        'tagTemplateString',
        'rootEnabled',
        'manufacturer',
        'modelName',
        'serialNumber',
        'osFamily',
        'osFamilyVersion',
        'firmwareVersion',
        'orientation',
        'resolutionWidth',
        'resolutionHeight',
        'volume',
        'ipAddress',
        'firstDns',
        'secondDns',
        'macAddress',
        'imei',
        'connectionType',
        'lastConnection',
        'storageSize',
        'usedStorageSize',
        'freeStorageSize',
        'networkIpMode',
        'networkWiFiSecurityMode',
        'networkWiFiEncryptionType',
        'networkWiFiSignalStrengthLevel',
        'networkGateway',
        'networkSubnetMask',
        'networkWiFiSsid',
        'reservedBytes',
        'usedBytes',
        'clientToServerResponseTimeMS',
        'serverToDsRequestTimeMS',
        'clientToServerRequestTimeMS',
        'downloadFromStorageSpeed',
    ]

    return keys.reduce((acc: FormattedParameters, key) => {
        if (parameters.hasOwnProperty(key)) {
            const value = parameters[key].toString()

            if (parameters[key] !== null && value.length && value !== 'undef') {
                acc[key] = value
            }
        }
        return acc
    }, {})
}

let screenHeight: number = 353
let showCount: number = 4
let timerCall: number | undefined
let timer: number | undefined
let screenshotUpdateTimer: ReturnType<typeof setTimeout> | null = null

const DisplaysInfoMethods = (p_: IDisplayInfoProps) => {
    const [state, setState] = useState<IState>({
        wakeOnLanModal: false,
        openPowerModal: '',
        sleepModeModal: '',
        active: [],
        showAll: false,
        removeAll: false,
        dsState: null,
        deviceTimes: null,
        startTime: null,
        deviceStartTime: null,
        times: null,
        showCleanMenu: false,
        getClientLogsMenu: false,
        externalStorages: null,
        buildInfo: null,
        clean: {
            files: true,
        },
        isUpdatingScreenShot: false,
        formattedParameters: {},
        licensesData: [],
        currentLicense: null,
        webEngineInfo: '',
        showMore: false,
        storageInfoLoading: true,
        storageInfo: null,
        networkInfoLoading: true,
        networkInfo: null,
        isConnectionSpeedLoading: false,
        connectionSpeed: null,
        connectionSpeedStartTimestamp: 0,
        isStorageConnectionSpeedLoading: false,
        hasStorageConnectionSpeedError: false,
        toggleStatusList: {
            mainRef: false,
            deviceRef: false,
            licensesRef: false,
            networkRef: false,
            storageRef: false,
            externalStorageRef: false,
            buildRef: false,
        },
        softResetModal: false,
        groupTreeInfo: null,
        groupsOpened: [],
        connectionError: false,
        showClearLogsWarning: false,
    })
    const screenRef = useRef<HTMLDivElement>(null)
    const mainRef = useRef<RefContent>({} as RefContent)
    const deviceRef = useRef<RefContent>({} as RefContent)
    const licensesRef = useRef<RefContent>({} as RefContent)
    const networkRef = useRef<RefContent>({} as RefContent)
    const storageRef = useRef<RefContent>({} as RefContent)
    const externalStorageRef = useRef<RefContent>({} as RefContent)
    const buildRef = useRef<RefContent>({} as RefContent)
    const groupTreeRef = useRef<RefContent>({} as RefContent)

    const getDsStorageInfo = (data: { digitalSignageId: number }) => {
        if (isStorageUpdateSupport(p_.selectedInfo?.platform)) {
            setState((prevState) => {
                return {
                    ...prevState,
                    storageInfoLoading: true,
                }
            })
            api.send('getDsStorageInfo', data).then((res) => {})
        } else {
            setState((prevState) => {
                return {
                    ...prevState,
                    storageInfoLoading: false,
                }
            })
        }
    }

    const setWebEngineInfo = (webEngineInfo: { content: string }) => {
        if (webEngineInfo && webEngineInfo.content) {
            setState((prevState) => {
                return {
                    ...prevState,
                    webEngineInfo: webEngineInfo.content,
                }
            })

            return
        }

        setState((prevState) => {
            return {
                ...prevState,
                webEngineInfo: translate('cantGetWebEngineInfo'),
            }
        })
    }

    const isNetworkUpdateSupport = (platformName: string) => {
        const supportList = [
            'ANDROID',
            'SSSP 2',
            'SSSP 3',
            'SSSP 4',
            'SSSP 5',
            'SSSP 6',
            'SSSP 7',
            'SSSP 10',
            'WEBOS 2',
            'WEBOS 3',
            'WEBOS 3.2',
            'WEBOS 4',
            'WEBOS 4.1',
            'WEBOS 6',
            'WEBOS 9',
            'LINUX',
            'LINUX x32',
            'LINUX x64',
            'RASPBERRY',
            'RASPBERRY_V3 x32',
            'RASPBERRY_V3 x64',
            'RASPBERRY_V4 x32',
            'RASPBERRY_V4 x64',
            'WINDOWS',
            'TIZEN 7',
            'WINDOWS x32',
            'WINDOWS x64',
            'WINDOWS 7 x32',
            'WINDOWS 7 x64',
        ]

        return supportList.includes(platformName)
    }

    const getNetworkInfo = (data: { id: number }) => {
        if (isNetworkUpdateSupport(p_.selectedInfo?.platform)) {
            setState((prevState) => {
                return {
                    ...prevState,
                    networkInfoLoading: true,
                }
            })

            api.send<{ id: number }, NonNullable<IState['networkInfo']>>('getNetworkInfo', data).then((res) => {
                setState((prevState) => {
                    return {
                        ...prevState,
                        networkInfoLoading: false,
                        networkInfo: res,
                    }
                })
            })
        } else {
            setState((prevState) => {
                return {
                    ...prevState,
                    networkInfoLoading: false,
                }
            })
        }
    }

    const showConnectionError = () => {
        setState((prevState) => {
            return {
                ...prevState,
                connectionError: true,
            }
        })
    }

    const getExternalStorageInfo = (data: { digitalSignageId: number }) => {
        api.send<{ digitalSignageId: number }, ExternalStorageInfo>('getExternalStoragesInfo', {
            digitalSignageId: data.digitalSignageId,
        }).then((res) => {
            setExternalStorages({
                externalStorages: res.externalStorages,
                externalStoragesUpdatedAt: res.externalStoragesUpdatedAt,
            })
        })
    }

    const onReceiveDsStorageInfo = (data: DSStorageInfo) => {
        if (data.digitalSignageId !== p_.selectedInfo.id) {
            return
        }

        setState((prevState) => {
            return {
                ...prevState,
                storageInfoLoading: false,
                storageInfo: data,
            }
        })
    }

    const onConnectionSpeedReceived = (data: { serverToDsRequestTimeMS: number }) => {
        const clientToServerResponseTimeMS = Date.now() - state.connectionSpeedStartTimestamp

        setState((prevState) => {
            const s_ = {
                ...prevState,
                connectionError: false,
                isConnectionSpeedLoading: false,
                connectionSpeed: {
                    ...prevState.connectionSpeed,
                    serverToDsRequestTimeMS: data.serverToDsRequestTimeMS,
                    clientToServerResponseTimeMS,
                    clientToServerRequestTimeMS: clientToServerResponseTimeMS - data.serverToDsRequestTimeMS,
                },
            }

            s_.formattedParameters = _formattingParameters(p_.selectedInfo, s_)

            return {
                ...s_,
            }
        })
    }

    const onStorageConnectionSpeedReceived = (data: {
        digitalSignageId?: number
        downloadFromStorageSpeed?: number
        error?: unknown
    }) => {
        if (data.digitalSignageId && data.digitalSignageId !== p_.selectedInfo.id) {
            return
        }

        if (data.downloadFromStorageSpeed) {
            setState((prevState) => {
                const s_ = {
                    ...prevState,
                    isStorageConnectionSpeedLoading: false,
                    connectionSpeed: {
                        ...prevState.connectionSpeed,
                        downloadFromStorageSpeed: data.downloadFromStorageSpeed,
                    },
                }
                s_.formattedParameters = _formattingParameters(p_.selectedInfo, s_)

                return {
                    ...s_,
                }
            })
        } else if (data.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    isStorageConnectionSpeedLoading: false,
                    hasStorageConnectionSpeedError: true,
                }
            })
        }
    }

    const onToggleGroup = (data: { id: number; parentId: number }) => {
        if (state.groupsOpened.some((item: { id: number }) => data.id === item.id)) {
            setState((prevState) => {
                return {
                    ...prevState,
                    groupsOpened: prevState.groupsOpened.filter(
                        (item: { id: number; parentId: number }) => item.id !== data.id
                    ),
                }
            })

            return
        }

        setState((prevState) => {
            return {
                ...prevState,
                groupsOpened: [...state.groupsOpened, data],
            }
        })
    }

    const toggleSingle = (value: boolean, name: string) => {
        setState((prevState) => {
            return {
                ...prevState,
                toggleStatusList: {
                    ...prevState.toggleStatusList,
                    [name]: value,
                },
            }
        })
    }

    const toggleAll = (open: boolean) => {
        setState((prevState) => {
            return {
                ...prevState,
                toggleStatusList: {
                    mainRef: open,
                    deviceRef: open,
                    licensesRef: open,
                    networkRef: open,
                    storageRef: open,
                    externalStorageRef: open,
                    buildRef: open,
                },
            }
        })

        const refs = [
            mainRef.current,
            deviceRef.current,
            licensesRef.current,
            networkRef.current,
            storageRef.current,
            externalStorageRef.current,
            buildRef.current,
        ]

        refs.forEach((ref) => {
            if (open) {
                ref.contentOpen && ref.contentOpen()
            } else {
                ref.contentClose && ref.contentClose()
            }
        })
    }

    const checkPowerButtonDisableStatus = (selected: ISelectedInfo) => {
        const noCommands = displaysHelpers.getAvailableCommands(selected).length === 0
        const noWakeOnLan = !displaysHelpers.availableWakeOnLan(selected.platform)
        const noSleepMode = !isSleepStatus(selected)
        const isOffline = isOfflineStatus(selected)

        if (noCommands) {
            return true
        }

        return noWakeOnLan && noSleepMode && isOffline
    }

    const enterSleepMode = (data: { digitalSignageId?: number; groupId?: number }, callback: () => void) => {
        if (!data.digitalSignageId && !data.groupId) return

        api.send('enterSleepMode', data).then(() => callback && callback())
    }

    const exitSleepMode = (data: { digitalSignageId?: number; groupId?: number }) => {
        if (!data.digitalSignageId && !data.groupId) return

        api.send('exitSleepMode', data).then(() => {
            setState((prevState) => {
                return {
                    ...prevState,
                    toggleStatusList: {
                        ...prevState.toggleStatusList,
                        active: [],
                    },
                }
            })
        })
    }

    const getSelectInfoStatus = (selectedInfo: ISelectedInfo) => {
        return selectedInfo ? selectedInfo.status : null
    }

    const isOfflineStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'offline'
    }

    const isErrorStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'error'
    }

    const isSleepStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'sleep'
    }

    const isOnlineStatus = (selectedInfo: ISelectedInfo) => {
        return getSelectInfoStatus(selectedInfo) === 'online'
    }

    const toggleRemoveAll = () => {
        setState((prevState) => {
            return {
                ...prevState,
                toggleStatusList: {
                    ...prevState.toggleStatusList,
                    removeAll: !prevState.removeAll,
                },
            }
        })
    }

    const enableRefreshAnimation = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isUpdatingScreenShot: true,
            }
        })
    }

    const setShowMore = () => {
        setState((prevState) => {
            return {
                ...prevState,
                showMore: true,
            }
        })
    }

    const showAll = () => {
        setState((prevState) => {
            return {
                ...prevState,
                showAll: !prevState.showAll,
            }
        })
    }

    const toggleShutdownMenu = (e: MouseEvent) => {
        e.stopPropagation()

        setState((prevState) => {
            return {
                ...prevState,
                showShutdownMenu: !prevState.showShutdownMenu,
                showDeleteMenu: false,
            }
        })
    }

    const toggleCleanMenu = (e: React.MouseEvent) => {
        e.stopPropagation()

        setState((prevState) => {
            return {
                ...prevState,
                showCleanMenu: !prevState.showCleanMenu,
            }
        })
    }

    const softResetIsSupported = (platform: string | null) => {
        const platformKey = 'WEBOS'
        const minVersion = 4.1

        if (platform && platform.includes(platformKey)) {
            const splitPlatform = platform.split(' ')

            if (splitPlatform.length === 2) {
                const version = +splitPlatform[1]

                if (!isNaN(version)) {
                    return version >= minVersion
                }
            }
        }

        return false
    }

    const openSoftResetModal = () => {
        setState((prevState) => {
            return {
                ...prevState,
                softResetModal: true,
            }
        })
    }

    const closeSoftResetModal = () => {
        setState((prevState) => {
            return {
                ...prevState,
                softResetModal: false,
            }
        })
    }

    const softReset = () => {
        api.send('dsSoftReset', { digitalSignageId: p_.selectedInfo.id }).then(() => closeSoftResetModal())
    }

    const toggleLoadLogsMenu = (e: React.MouseEvent) => {
        e.stopPropagation()

        setState((prevState) => {
            return {
                ...prevState,
                getClientLogsMenu: !prevState.getClientLogsMenu,
            }
        })
    }

    const toggleDeleteMenu = (e: MouseEvent) => {
        e.stopPropagation()

        setState((prevState) => {
            return {
                ...prevState,
                showDeleteMenu: !prevState.showDeleteMenu,
                showShutdownMenu: false,
            }
        })
    }

    const getDsState = () => {
        api.send('fDsGetState', { id: p_.selectedInfo.id }, { hideLoader: true })
    }

    const setDsState = (dsState: DSState) => {
        console.log(dsState)
        if (p_.selectedInfo && p_.selectedInfo.id !== dsState.id) {
            // backend посылает информацию о разных экранах f_ds_get_state
            return
        }

        const data: {
            deviceStartTime?: string | null
            dsState: DSState
            startTime: string | null
        } = {
            dsState: dsState,
            startTime: null,
        }

        delete (data.dsState as { id?: number }).id

        if (dsState.hasOwnProperty('startTime')) {
            data.startTime = dsState.startTime
                ? moment(parseInt(dsState.startTime, 10)).format('YYYY-MM-DD HH:mm:ss')
                : null
        }

        if (dsState.hasOwnProperty('deviceStartTime')) {
            data.deviceStartTime = dsState.deviceStartTime
                ? moment(parseInt(dsState.deviceStartTime, 10)).format('YYYY-MM-DD HH:mm:ss')
                : null
        }

        setState((prevState) => {
            return {
                ...prevState,
                ...data,
            }
        })
    }

    const onReceiveBuildInfo = (data: { buildInfo: string; digitalSignageId: number }) => {
        if (data.digitalSignageId === p_.selectedInfo.id) {
            if (data.buildInfo) {
                setState((prevState) => {
                    return {
                        ...prevState,
                        buildInfo: JSON.parse(data.buildInfo) as BuildInfo,
                    }
                })
            }
        }
    }

    const getDsStateFormat = (dsState: DSState, key: string) => {
        const { selectedInfo } = p_
        const { platform } = selectedInfo
        const percentageByPlatforms = ['SSSP 2', 'SSSP 3']
        const parameter = dsState[key]
        const timezoneOffsetHours = Math.abs((selectedInfo.timezoneOffset || 0) / 60)
        const timezoneOffsetSign = (selectedInfo.timezoneOffset || 0) >= 0 ? '+' : '-'
        const timezoneOffsetLabel = `UTC ${timezoneOffsetSign}${String(timezoneOffsetHours).padStart(2, '0')}:00`

        switch (key) {
            case 'cpuLoad':
                return `${parameter}%`
            case 'cpuTemperature':
                return `${parameter}°`
            case 'freeRAM':
                if (percentageByPlatforms.includes(platform)) {
                    return `${translate('free')} ${parameter}%`
                } else {
                    return `${translate('free')} ${filesize(parseFloat(String(parameter)))}`
                }
            case 'currentDate':
                return moment
                    .tz(
                        moment.utc(parameter == null ? undefined : parameter).toISOString(),
                        resolveLegacyTimezone(selectedInfo.timezone) || 'Europe/Moscow'
                    )
                    .format('HH:mm:ss DD-MM-YYYY')
            case 'startTime':
                return moment(parameter == null ? undefined : parameter).format('HH:mm:ss DD-MM-YYYY')
            case 'deviceStartTime':
                return moment(parameter == null ? undefined : parameter).format('HH:mm:ss DD-MM-YYYY')
            case 'availableRfid':
                return Array.isArray(parameter) ? parameter.join(', ') : parameter
            case 'timezone':
                return selectedInfo.timezone
                    ? `${selectedInfo.timezone} ${timezoneOffsetLabel}`
                    : translate('unavailable')
            case 'powerSource':
                return translate(`powerSource${parameter}`)
            case 'batteryLevel':
                return `${parameter}%`
            default:
                return parameter
        }
    }

    const getDsStateLabel = (key: string) => {
        switch (key) {
            case 'startTime':
                return translate('appStartTime')
            default:
                return translate(key)
        }
    }

    const timerCallback = () => {
        if (!state.startTime) {
            return
        }

        const current = moment()
        const uptime = moment(state.startTime)
        const diff = current.diff(uptime, 'seconds')
        const diffDays = current.diff(uptime, 'days').toString()
        const diffTimes = moment().startOf('day').seconds(diff).format('HH:mm:ss')

        setState((prevState) => {
            return {
                ...prevState,
                startTime: uptime.format('YYYY-MM-DD HH:mm:ss'),
                times: {
                    diffTimes,
                    diffDays,
                },
            }
        })
    }

    const deviceTimerCallback = () => {
        if (!state.deviceStartTime) {
            return
        }

        const current = moment()
        const uptime = moment(state.deviceStartTime)
        const diff = current.diff(uptime, 'seconds')
        const diffDays: string = current.diff(uptime, 'days').toString()

        const diffTimes: string = moment().startOf('day').seconds(diff).format('HH:mm:ss')

        setState((prevState) => {
            return {
                ...prevState,
                deviceStartTime: uptime.format('YYYY-MM-DD HH:mm:ss'),
                deviceTimes: {
                    diffTimes,
                    diffDays,
                },
            }
        })
    }

    const timersCallback = () => {
        timerCallback()
        deviceTimerCallback()
    }

    const getLicenses = (companyId: number) => {
        api.send<{ sort: [string, string][]; limit: number; companyId: number }, { data: LicenseResponseItem[] }>(
            'getLicenseByFilters',
            {
                sort: [['licenseId', 'ASC']],
                limit: 1000,
                companyId: companyId,
            }
        ).then((res) => {
            let optionFormat = res.data
                .filter((data) => data.activeQ)
                .map((opt) => ({
                    id: opt.licenseId,
                    name: licenseType(opt) + ' ' + rateTypeRename(opt.rateType || ''),
                    vacantCount: opt.vacantCount,
                    expiredAt: opt.expiredAt,
                    paidQ: opt.paidQ,
                }))

            setState((prevState) => {
                return {
                    ...prevState,
                    licensesData: optionFormat,
                }
            })
        })
    }

    const setExternalStorages = (data: ExternalStorageInfo) => {
        if (!data) return

        setState((prevState) => {
            return {
                ...prevState,
                externalStorages: {
                    externalStorages: data.externalStorages,
                    externalStoragesUpdatedAt: data.externalStoragesUpdatedAt,
                },
            }
        })
    }

    const fallbackCopyToClipboard = (text: string, clipboardResultToastOptions: ToastOptions) => {
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        try {
            document.execCommand('copy')
            Toast.info(translate('textWasCopied'), clipboardResultToastOptions)
        } catch (err) {
            Toast.error(translate('copyToClipboardError'), clipboardResultToastOptions)
        }
        document.body.removeChild(textArea)
    }

    const copyToClipboard = (data: string) => {
        const clipboardResultToastOptions = {
            autoClose: 3000,
            position: 'bottom-left',
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(data).then(
                () => {
                    Toast.info(translate('copiedToClipboard'), clipboardResultToastOptions)
                },
                () => {
                    fallbackCopyToClipboard(data, clipboardResultToastOptions)
                }
            )
        } else {
            fallbackCopyToClipboard(data, clipboardResultToastOptions)
        }
    }

    const onAcceptModal = () => {
        let data: {
            id?: number
            groupId?: number
        } = {
            id: p_.selectedInfo.id,
        }

        if (helpersDigitalSignages.isFolder(p_.selectedInfo)) {
            data = {
                groupId: p_.selectedInfo.id,
            }
        }

        api.send('shutdownDisplay', data)

        closeAcceptModal()
    }

    const closeAcceptModal = () => {
        setState((prevState) => {
            return {
                ...prevState,
                openPowerModal: '',
            }
        })
    }

    const onFDsGetState = (dsState: DSState) => {
        setDsState(dsState)
    }

    useEffect(() => {
        getLicenses(p_.user)

        const listenersId: string[] = []

        if (p_.selectedInfo && p_.selectedInfo.type !== 'group') {
            getExternalStorageInfo({ digitalSignageId: p_.selectedInfo.id })
        }

        api.addObserver('dsBuildInfoReceived', onReceiveBuildInfo, listenersId)
        api.addObserver('fDsGetState', onFDsGetState, listenersId)
        api.addObserver('webEngineInfoReceived', setWebEngineInfo, listenersId)
        api.addObserver(
            'digitalSignageUpdated',
            (res: ExternalStorageInfo & { digitalSignageId?: number }) => {
                if (res.digitalSignageId && res.digitalSignageId === p_.selectedInfo.id) {
                    setExternalStorages({
                        externalStorages: res.externalStorages,
                        externalStoragesUpdatedAt: res.externalStoragesUpdatedAt,
                    })
                }
            },
            listenersId
        )
        api.addObserver(
            'digitalSignageScreenshotCreated',
            () => {
                if (screenshotUpdateTimer) {
                    clearTimeout(screenshotUpdateTimer)
                }

                screenshotUpdateTimer = setTimeout(() => {
                    screenshotUpdateTimer = null
                    setState((prevState) => {
                        return {
                            ...prevState,
                            isUpdatingScreenShot: false,
                        }
                    })
                }, 1000)
            },
            listenersId
        )

        api.addObserver('dsStorageInfoReceived', onReceiveDsStorageInfo, listenersId)
        api.addObserver('storageConnectionSpeedReceived', onStorageConnectionSpeedReceived, listenersId)

        if (p_.selectedInfo.online && !helpersDigitalSignages.isFolder(p_.selectedInfo)) {
            getDsState()
            timersCallback()

            window.clearInterval(timerCall)
            timerCall = window.setInterval(getDsState, 10000)

            window.clearInterval(timer)
            timer = window.setInterval(timersCallback, 1000)
        }

        if (p_.selectedInfo.online) {
            const payload = {
                digitalSignageId: p_.selectedInfo.id,
            }
            getDsStorageInfo(payload)
            getNetworkInfo({ id: p_.selectedInfo.id })
        }

        setState((prevState) => {
            return {
                ...prevState,
                formattedParameters: _formattingParameters(p_.selectedInfo, state),
            }
        })

        const getScreenHeight = () => {
            if (screenRef.current) {
                const formula = 1.78

                screenHeight = screenRef.current.offsetWidth / formula
            }
        }

        if (p_.selectedInfo.type === 'digitalSignage') {
            api.send('getDsBuildInfo', { digitalSignageId: p_.selectedInfo.id })
        }

        if (screenRef.current) {
            window.addEventListener('resize', getScreenHeight)
        }

        if (p_.selectedInfo.type === 'group') {
            api.send<{ groupId: number }, IState['groupTreeInfo']>('getGroupTreeInfo', {
                groupId: p_.selectedInfo.id,
            }).then((res) => {
                if (!res) return

                setState((prevState) => {
                    return {
                        ...prevState,
                        groupTreeInfo: res,
                    }
                })
            })
        }

        return () => {
            window.clearInterval(timerCall)
            window.clearInterval(timer)

            if (screenshotUpdateTimer) {
                clearTimeout(screenshotUpdateTimer)
                screenshotUpdateTimer = null
            }

            listenersId.forEach((id) => api.removeObserver(id))

            if (screenRef.current) {
                window.removeEventListener('resize', getScreenHeight)
            }
        }
    }, [state.startTime, state.deviceStartTime])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver('connectionSpeedReceived', onConnectionSpeedReceived, listenersId)

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [state.connectionSpeedStartTimestamp])

    useEffect(() => {
        if (!p_.selectedInfo.online) {
            window.clearInterval(timerCall)

            setState((prevState) => {
                return {
                    ...prevState,
                    dsState: null,
                }
            })
        }
    }, [p_.selectedInfo])

    const copyParameterValueToClipboard = (value: string | number | boolean | null) => () => {
        copyToClipboard(String(value ?? ''))
    }

    const copyParametersToClipboard = () => {
        let parametersString = Object.entries(state.formattedParameters).reduce((acc, [key, value]) => {
            acc += `${translate(key)}: ${value.length ? value.replace(/(\<(\/?[^>]+)>)/g, '') + ' ' : value}\n`

            return acc
        }, '')

        if (state.buildInfo) {
            parametersString += Object.entries(state.buildInfo).reduce((acc, [key, value]) => {
                acc += `${translate(key)}: ${value}\n`

                return acc
            }, '')
        }

        copyToClipboard(parametersString)
    }

    const closeWebEngineInfo = () => {
        setState((prevState) => {
            return {
                ...prevState,
                webEngineInfo: '',
            }
        })
    }

    const isMultipleClearSupport = (platformName: string) => {
        const supportList = [
            'LINUX',
            'LINUX x32',
            'LINUX x64',
            'RASPBERRY',
            'RASPBERRY_V3 x32',
            'RASPBERRY_V3 x64',
            'RASPBERRY_V4 x32',
            'RASPBERRY_V4 x64',
            'WINDOWS',
            'WINDOWS x32',
            'WINDOWS x64',
            'WINDOWS 7 x32',
            'WINDOWS 7 x64',
            'ANDROID',
        ]

        return supportList.includes(platformName)
    }

    const isWebEngineInfoSupport = (platformName: string) => {
        const supportList = [
            'LINUX',
            'LINUX x32',
            'LINUX x64',
            'RASPBERRY',
            'RASPBERRY_V3 x32',
            'RASPBERRY_V3 x64',
            'RASPBERRY_V4 x32',
            'RASPBERRY_V4 x64',
            'WINDOWS',
            'WINDOWS x32',
            'WINDOWS x64',
            'WINDOWS 7 x32',
            'WINDOWS 7 x64',
            'ANDROID',
            'BRIGHTSIGN',
            'SSSP 2',
            'SSSP 3',
            'SSSP 4',
            'SSSP 5',
            'SSSP 6',
            'SSSP 7',
            'SSSP 10',
            'WEBOS 2',
            'WEBOS 3',
            'WEBOS 3.2',
            'WEBOS 4',
            'WEBOS 4.1',
            'WEBOS 6',
            'WEBOS 9',
            'TIZEN 7',
        ]

        return supportList.includes(platformName)
    }

    const isMonitoringSupport = (platformName: string) => {
        const supportList = [
            'BRIGHTSIGN',
            'SSSP 2',
            'SSSP 3',
            'SSSP 4',
            'SSSP 5',
            'SSSP 6',
            'SSSP 7',
            'SSSP 10',
            'WEBOS 2',
            'WEBOS 3',
            'WEBOS 3.2',
            'WEBOS 4',
            'WEBOS 4.1',
            'WEBOS 6',
            'WEBOS 9',
            'LINUX',
            'LINUX x32',
            'LINUX x64',
            'RASPBERRY',
            'RASPBERRY_V3 x32',
            'RASPBERRY_V3 x64',
            'RASPBERRY_V4 x32',
            'RASPBERRY_V4 x64',
            'WINDOWS',
            'WINDOWS x32',
            'WINDOWS x64',
            'WINDOWS 7 x32',
            'WINDOWS 7 x64',
            'ANDROID',
            'TIZEN 7',
        ]

        return supportList.includes(platformName)
    }

    const isStorageUpdateSupport = (platformName: string) => {
        const supportList = [
            'ANDROID',
            'SSSP 2',
            'SSSP 3',
            'SSSP 4',
            'SSSP 5',
            'SSSP 6',
            'SSSP 7',
            'SSSP 10',
            'WEBOS 2',
            'WEBOS 3',
            'WEBOS 3.2',
            'WEBOS 4',
            'WEBOS 4.1',
            'WEBOS 6',
            'WEBOS 9',
            'TIZEN 7',
            'LINUX',
            'LINUX x32',
            'LINUX x64',
            'RASPBERRY',
            'RASPBERRY_V3 x32',
            'RASPBERRY_V3 x64',
            'RASPBERRY_V4 x32',
            'RASPBERRY_V4 x64',
            'WINDOWS',
            'WINDOWS x32',
            'WINDOWS x64',
            'WINDOWS 7 x32',
            'WINDOWS 7 x64',
        ]

        return supportList.includes(platformName)
    }

    const getConnectionsSpeed = () => {
        setState((prevState) => {
            return {
                ...prevState,
                connectionError: false,
                isConnectionSpeedLoading: true,
                connectionSpeedStartTimestamp: Date.now(),
            }
        })

        api.send('getConnectionSpeed', { digitalSignageId: p_.selectedInfo.id })
    }

    const getStorageConnectionSpeed = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isStorageConnectionSpeedLoading: true,
            }
        })

        api.send('getStorageConnectionSpeed', { digitalSignageId: p_.selectedInfo.id }).catch((e) => {
            setState((prevState) => {
                return {
                    ...prevState,
                    hasStorageConnectionSpeedError: true,
                }
            })
        })
    }

    return {
        state,
        setState,
        screenRef,
        mainRef,
        deviceRef,
        licensesRef,
        networkRef,
        storageRef,
        externalStorageRef,
        buildRef,
        groupTreeRef,
        screenHeight,
        enableRefreshAnimation,
        toggleLoadLogsMenu,
        checkPowerButtonDisableStatus,
        isOfflineStatus,
        isOnlineStatus,
        isErrorStatus,
        isSleepStatus,
        exitSleepMode,
        isMonitoringSupport,
        softResetIsSupported,
        openSoftResetModal,
        isWebEngineInfoSupport,
        closeSoftResetModal,
        softReset,
        getDsStateLabel,
        getDsStateFormat,
        copyParametersToClipboard,
        getDsStorageInfo,
        getExternalStorageInfo,
        getNetworkInfo,
        copyParameterValueToClipboard,
        getConnectionsSpeed,
        getStorageConnectionSpeed,
        toggleSingle,
        showConnectionError,
        setShowMore,
        closeWebEngineInfo,
        enterSleepMode,
        onToggleGroup,
        toggleCleanMenu,
        toggleAll,
    }
}

export default DisplaysInfoMethods
